import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import type { INotFoundBlockProps } from "./NotFoundBlock.props";

const DEFAULT_STATUS_CODE = 404;
const DEFAULT_TITLE = "Page not found";
const DEFAULT_DESCRIPTION = "The page you are looking for does not exist or has been moved.";
const DEFAULT_HOME_HREF = "/";
const DEFAULT_HOME_LABEL = "Go home";
const TITLE_ID = "kaaj-not-found-title";

export const NotFoundBlock = ({
  description = DEFAULT_DESCRIPTION,
  homeHref = DEFAULT_HOME_HREF,
  homeLabel = DEFAULT_HOME_LABEL,
  parentClass,
  statusCode = DEFAULT_STATUS_CODE,
  title = DEFAULT_TITLE,
}: INotFoundBlockProps) => {
  const Link = resolveKaajPrimitive("Link");
  const hasHomeAction = Boolean(homeHref && homeLabel);

  return (
    <section
      aria-labelledby={TITLE_ID}
      className={cn("relative isolate overflow-hidden py-16 sm:py-20 lg:py-28", parentClass)}
      data-kaaj-block="notFound"
    >
      <div className="kaaj-container">
        <div className="relative overflow-hidden rounded-[2rem] border border-[#D9E1FF] bg-[#FAFBFF] px-6 py-10 shadow-[0_24px_80px_rgba(33,68,255,0.10)] sm:px-10 sm:py-14 lg:px-14 lg:py-16">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-28 -top-36 size-96 rounded-full bg-[#F6F1FF]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-36 left-1/3 size-72 rounded-full bg-[#FFEAF9]/65 blur-3xl"
          />

          <div className="relative grid min-w-0 items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.78fr)] lg:gap-16">
            <div className="flex min-w-0 max-w-3xl flex-col items-start">
              <p className="mb-5 text-sm font-bold text-[#2144FF]">Error {statusCode}</p>
              <h1
                className="text-balance text-[clamp(2.45rem,7vw,4rem)] font-extrabold leading-[1.04] tracking-[-0.04em] text-[#151927] [overflow-wrap:anywhere]"
                id={TITLE_ID}
              >
                {title}
              </h1>
              <p className="mt-5 max-w-[60ch] text-pretty text-base leading-7 text-[#4B5568] sm:text-lg sm:leading-8">
                {description}
              </p>

              {hasHomeAction ? (
                <Link
                  aria-label={homeLabel}
                  className="mt-8 inline-flex min-h-11 items-center justify-center rounded-full bg-[#2144FF] px-6 text-sm font-bold text-white shadow-[0_12px_30px_rgba(33,68,255,0.24)] transition-colors hover:bg-[#132EDB] focus-visible:ring-4 focus-visible:ring-[#8FA3FF]/35 focus-visible:outline-none"
                  href={homeHref}
                >
                  <span>{homeLabel}</span>
                  <span aria-hidden="true" className="ml-2">
                    →
                  </span>
                </Link>
              ) : null}
            </div>

            <div
              aria-hidden="true"
              className="relative overflow-hidden rounded-[1.75rem] border border-[#D9E1FF] bg-white p-3 shadow-[0_18px_55px_rgba(33,68,255,0.11)]"
            >
              <div className="rounded-[1.25rem] border border-[#D9E1FF] bg-[#F6F1FF]/55 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-4 border-b border-[#D9E1FF] pb-4">
                  <span className="text-sm font-extrabold text-[#151927]">Route status</span>
                  <span className="inline-flex items-center gap-2 text-xs font-bold text-[#4B5568]">
                    <span className="size-2 rounded-full bg-[#FFD86B]" />
                    Unavailable
                  </span>
                </div>

                <div className="relative flex min-h-56 items-center justify-between gap-4 py-10">
                  <div className="relative z-10 flex size-16 items-center justify-center rounded-2xl border border-[#D9E1FF] bg-white text-sm font-extrabold text-[#2144FF] shadow-sm">
                    Home
                  </div>
                  <div className="absolute inset-x-14 top-1/2 border-t-2 border-dashed border-[#8FA3FF]" />
                  <div className="relative z-20 flex size-16 items-center justify-center rounded-full border-4 border-white bg-[#2144FF] text-sm font-extrabold text-white shadow-[0_10px_30px_rgba(33,68,255,0.3)]">
                    {statusCode}
                  </div>
                  <div className="relative z-10 flex size-16 items-center justify-center rounded-2xl border border-[#D9E1FF] bg-white text-center text-xs font-extrabold leading-4 text-[#4B5568] shadow-sm">
                    Missing
                    <br />
                    page
                  </div>
                </div>

                <p className="border-t border-[#D9E1FF] pt-4 text-sm leading-6 text-[#4B5568]">
                  The requested route could not be resolved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

NotFoundBlock.displayName = "Block::NotFoundBlock";
